'use client'

import { useId, type InputHTMLAttributes } from 'react'
import { Controller } from 'react-hook-form'

import MedantePhoneInput from '@/_components/ui/phone-input'
import { cn } from '@/_lib/utils'

import { MFieldError } from './m-field-error'

import type { Control, FieldPath, FieldValues, RegisterOptions, UseFormRegisterReturn } from 'react-hook-form'

/** Boomex `.form .input_text` + floating label — `_box.scss` */
const inputBoomexBase =
	'peer w-full rounded-[10px] border border-solid border-medante-purple-dark bg-medante-background text-sm font-semibold text-medante-purple-dark ' +
	'placeholder:text-transparent outline-none transition-[color,border-color] ' +
	'pt-[23px] pb-[14px] pl-[25px] pr-6 max-md:pt-[18px] max-md:text-[13px] ' +
	'md:rounded-[14px] ' +
	'focus:border-medante-purple-dark focus:ring-0 focus-visible:ring-0 ' +
	'[&:-webkit-autofill]:[-webkit-text-fill-color:#4a337a] [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#fdfbf7] [&:-webkit-autofill]:[transition:background-color_600000s_ease] ' +
	'disabled:opacity-50'

const inputBoomexError = 'border-[#fe2b20] text-[#fe2b20]'

const floatLabelBase =
	'pointer-events-none absolute left-[25px] top-[19px] z-[1] max-w-[calc(100%-2rem)] origin-top-left truncate font-normal transition-transform duration-200 ease-out max-md:top-[15px] max-md:text-[13px] ' +
	'peer-focus:-translate-y-[10px] peer-focus:scale-90 ' +
	'peer-[&:not(:placeholder-shown)]:-translate-y-[10px] peer-[&:not(:placeholder-shown)]:scale-90 ' +
	'peer-[&:-webkit-autofill]:-translate-y-[10px] peer-[&:-webkit-autofill]:scale-90'

/** Phone row: label stays shrunk (field always has country prefix). */
const phoneLabelShrunk =
	'pointer-events-none absolute left-[52px] top-2 z-[1] max-w-[calc(100%-3.5rem)] origin-top-left scale-90 truncate text-[13px] font-normal leading-tight max-md:left-[50px] max-md:top-1.5 max-md:text-[12px]'

type MInputPhoneProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
	type: 'phone'
	name: TName
	control: Control<TFieldValues>
	rules?: RegisterOptions<TFieldValues, TName>
	label: string
	required?: boolean
	error?: string
	inputClassName?: string
}

type MInputNativeProps = {
	label: string
	required?: boolean
	error?: string
	registration: UseFormRegisterReturn
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'placeholder' | 'type'> & {
	type?: Exclude<InputHTMLAttributes<HTMLInputElement>['type'], 'phone'>
}

export type MInputProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = MInputNativeProps | MInputPhoneProps<TFieldValues, TName>

function isPhoneProps<T extends FieldValues, N extends FieldPath<T>>(
	p: MInputProps<T, N>
): p is MInputPhoneProps<T, N> {
	return 'type' in p && p.type === 'phone'
}

function MInputPhone<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
	name,
	control,
	rules,
	label,
	required,
	error,
	inputClassName
}: MInputPhoneProps<TFieldValues, TName>) {
	const inputId = useId()

	return (
		<div>
			<Controller
				name={name}
				control={control}
				rules={rules}
				render={({ field }) => (
					<div className='relative'>
						<MedantePhoneInput
							id={inputId}
							value={field.value || undefined}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
							placeholder=' '
							className={cn(
								'phone-input-order-flow',
								error && 'phone-input-order-flow--error',
								inputClassName
							)}
						/>
						<label
							htmlFor={inputId}
							className={cn(
								phoneLabelShrunk,
								error ? 'text-[#fe2b20]' : 'text-medante-purple-dark'
							)}
						>
							{label}
							{required && <span className='text-[#fe2b20]'>*</span>}
						</label>
					</div>
				)}
			/>
			<MFieldError message={error} />
		</div>
	)
}

function MInputNative({
	label,
	required,
	error,
	registration,
	className,
	type = 'text',
	...inputProps
}: MInputNativeProps) {
	const id = useId()
	const invalid = Boolean(error)

	return (
		<div>
			<div className='relative'>
				<input
					id={id}
					type={type}
					className={cn(inputBoomexBase, invalid && inputBoomexError, className)}
					placeholder=' '
					aria-invalid={invalid}
					{...registration}
					{...inputProps}
				/>
				<label
					htmlFor={id}
					className={cn(
						floatLabelBase,
						'text-sm text-medante-purple-dark',
						invalid && 'text-[#fe2b20]'
					)}
				>
					{label}
					{required && <span className='text-[#fe2b20]'>*</span>}
				</label>
			</div>
			<MFieldError message={error} />
		</div>
	)
}

/**
 * Medante floating-label text field, or `type="phone"` for the RHF-controlled phone input.
 */
export function MInput<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
	props: MInputProps<TFieldValues, TName>
) {
	if (isPhoneProps(props)) {
		return <MInputPhone {...props} />
	}
	return <MInputNative {...props} />
}
